import React, { useState } from 'react'
import { Download, Copy } from 'lucide-react'

interface IRMetadataDisplayProps {
  result: any;
  theme?: 'light' | 'dark';
  showToast?: (message: string) => void;
  addToResources?: (resource: any) => void;
  showHeader?: boolean;
}

const IRMetadataDisplay: React.FC<IRMetadataDisplayProps> = ({ result, theme = 'light', showToast, addToResources, showHeader = true }) => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({})
  const isDarkMode = theme === 'dark'

  // Helper function to copy text to clipboard
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast?.('Copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy:', error);
      showToast?.('Failed to copy to clipboard');
    }
  };

  // Parse JSON if result is a string
  let parsedResult = result
  if (typeof result === 'string') {
    try {
      parsedResult = JSON.parse(result)
    } catch (e) {
      // If parsing fails, show raw string
      return (
        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-50 text-gray-700'}`}>
          <div className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-200' : 'text-gray-800'} whitespace-pre-wrap`}>
            {result}
          </div>
        </div>
      )
    }
  }

  // Handle both direct result and nested output structure
  const metadata = parsedResult?.output || parsedResult

  // Check if this is IR metadata (has typical metadata fields)
  const isIRMetadata = metadata && typeof metadata === 'object' && (
    metadata.title ||
    metadata.abstract ||
    metadata.inventors ||
    metadata.assignee ||
    metadata.filing_date ||
    metadata.technology_field ||
    metadata.key_features
  );

  if (!isIRMetadata) {
    return null; // Not IR metadata, let other components handle it
  }

  if (!metadata || typeof metadata !== 'object') {
    return null;
  }

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }))
  }

  const sectionConfigs = {
    basic_info: { title: 'Basic Information', emoji: '📋', fields: ['title', 'abstract', 'technology_field'] },
    parties: { title: 'Parties & Dates', emoji: '👥', fields: ['inventors', 'assignee', 'attorney_docket'] },
    dates: { title: 'Filing Information', emoji: '📅', fields: ['filing_date', 'priority_date', 'application_number'] },
    features: { title: 'Key Features', emoji: '⭐', fields: ['key_features'] },
    related: { title: 'Related Applications', emoji: '🔗', fields: ['related_applications'] }
  }

  const exportToDocx = () => {
    try {
      let docContent = `
        <html>
          <head>
            <title>Invention Record Metadata</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.6; }
              h1 { color: #333; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; font-size: 18px; }
              h2 { color: #666; margin-top: 30px; margin-bottom: 15px; border-bottom: 1px solid #ddd; padding-bottom: 5px; font-size: 16px; }
              h3 { color: #333; margin-top: 20px; margin-bottom: 10px; font-size: 14px; }
              .section { margin: 25px 0; }
              .field-value { margin: 10px 0; }
              ul { margin: 8px 0; padding-left: 20px; }
              li { margin: 4px 0; }
              .separator { border-top: 3px solid #ddd; margin: 30px 0; }
            </style>
          </head>
          <body>
            <h1>Invention Record Metadata</h1>
      `;

      // Generate sections
      Object.entries(sectionConfigs).forEach(([sectionKey, config]) => {
        const hasData = config.fields.some(field => metadata[field] && (Array.isArray(metadata[field]) ? metadata[field].length > 0 : true));
        if (!hasData) return;

        docContent += `<div class="section">`;
        docContent += `<h1>${config.title}</h1>`;
        
        config.fields.forEach(field => {
          const value = metadata[field];
          if (!value || (Array.isArray(value) && value.length === 0)) return;

          const fieldLabel = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          docContent += `<h2>${fieldLabel}</h2>`;

          if (Array.isArray(value)) {
            if (value.length > 0) {
              docContent += '<ul>';
              value.forEach(item => {
                docContent += `<li>${typeof item === 'object' ? JSON.stringify(item) : item}</li>`;
              });
              docContent += '</ul>';
            }
          } else {
            docContent += `<div class="field-value">${value}</div>`;
          }
        });
        
        docContent += `</div>`;
        docContent += `<div class="separator"></div>`;
      });

      docContent += '</body></html>';

      // Create and download the file
      const blob = new Blob([docContent], { type: 'application/msword' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'invention-record-metadata.doc';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      if (showToast) {
        showToast('Invention Record Metadata exported to Word document');
      }
    } catch (error) {
      console.error('Error exporting IR metadata to DOCX:', error);
      if (showToast) {
        showToast('Error exporting IR metadata');
      }
    }
  }

  const formatFieldValue = (field, value) => {
    if (!value) return 'Not specified'
    
    if (Array.isArray(value)) {
      if (value.length === 0) return 'None specified'
      return (
        <ul className="list-disc pl-5 mt-1 space-y-1">
          {value.map((item, index) => (
            <li key={index} className="leading-relaxed">
              {typeof item === 'object' ? JSON.stringify(item) : item}
            </li>
          ))}
        </ul>
      )
    }
    
    if (field === 'abstract' && typeof value === 'string' && value.length > 100) {
      return <div className="leading-relaxed">{value}</div>
    }
    
    return value
  }

  const renderSection = (sectionKey, config) => {
    const hasData = config.fields.some(field => metadata[field] && (Array.isArray(metadata[field]) ? metadata[field].length > 0 : true))
    if (!hasData) return null

    const isExpanded = expandedSections[sectionKey] !== false // Default to expanded

    return (
      <div key={sectionKey} className={`mb-6 border rounded-lg ${isDarkMode ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-white'}`}>
        {/* Section Header */}
        <div 
          className={`flex items-center justify-between p-4 cursor-pointer ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}
          onClick={() => toggleSection(sectionKey)}
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">{config.emoji}</span>
            <div>
              <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {config.title}
              </h3>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {config.fields.filter(field => metadata[field] && (Array.isArray(metadata[field]) ? metadata[field].length > 0 : true)).length} field{config.fields.filter(field => metadata[field] && (Array.isArray(metadata[field]) ? metadata[field].length > 0 : true)).length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {isExpanded ? '▲' : '▼'}
          </span>
        </div>

        {/* Section Content */}
        {isExpanded && (
          <div className={`px-4 pb-4 border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
            <div className="space-y-4">
              {config.fields.map(field => {
                const value = metadata[field]
                if (!value || (Array.isArray(value) && value.length === 0)) return null

                const fieldLabel = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())

                return (
                  <div key={field} className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <div className="flex items-start justify-between mb-2">
                      <span className={`text-sm font-medium px-2 py-1 rounded ${isDarkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
                        {fieldLabel}
                      </span>
                      <button
                        onClick={() => copyToClipboard(Array.isArray(value) ? value.join('\n') : value.toString())}
                        className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                      >
                        <Copy className="w-3 h-3" />
                        Copy Field
                      </button>
                    </div>
                    <div className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                      {formatFieldValue(field, value)}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Section Actions */}
            <div className="flex gap-2 mt-4 pt-3 border-t border-gray-300 dark:border-gray-600">
              <button
                onClick={() => {
                  const sectionText = config.fields
                    .filter(field => metadata[field] && (Array.isArray(metadata[field]) ? metadata[field].length > 0 : true))
                    .map(field => {
                      const fieldLabel = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                      const value = metadata[field]
                      const formattedValue = Array.isArray(value) ? value.join('\n- ') : value
                      return `${fieldLabel}: ${formattedValue}`
                    }).join('\n\n');
                  copyToClipboard(sectionText);
                }}
                className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
              >
                <Copy className="w-3 h-3" />
                Copy Section
              </button>
              {addToResources && (
                <button
                  onClick={() => {
                    const sectionText = `# ${config.title}\n\n${config.fields
                      .filter(field => metadata[field] && (Array.isArray(metadata[field]) ? metadata[field].length > 0 : true))
                      .map(field => {
                        const fieldLabel = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                        const value = metadata[field]
                        const formattedValue = Array.isArray(value) ? '- ' + value.join('\n- ') : value
                        return `## ${fieldLabel}\n\n${formattedValue}`
                      }).join('\n\n')}`
                    addToResources({
                      type: 'ir_metadata_section',
                      title: config.title,
                      content: sectionText,
                      source: 'IR Metadata Extraction Task'
                    })
                    showToast?.('Section added to resources!')
                  }}
                  className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-green-700 hover:bg-green-600 text-green-100' : 'bg-green-100 hover:bg-green-200 text-green-800'}`}
                >
                  Add to Resources
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    )
  }

  // Expand/Collapse All Controls
  const allSections = Object.keys(sectionConfigs).filter(sectionKey => 
    sectionConfigs[sectionKey].fields.some(field => metadata[field] && (Array.isArray(metadata[field]) ? metadata[field].length > 0 : true))
  )
  
  const allSectionsText = Object.entries(sectionConfigs)
    .filter(([sectionKey, config]) => 
      config.fields.some(field => metadata[field] && (Array.isArray(metadata[field]) ? metadata[field].length > 0 : true))
    )
    .map(([sectionKey, config]) => {
      return `# ${config.title}\n\n${config.fields
        .filter(field => metadata[field] && (Array.isArray(metadata[field]) ? metadata[field].length > 0 : true))
        .map(field => {
          const fieldLabel = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
          const value = metadata[field]
          const formattedValue = Array.isArray(value) ? '- ' + value.join('\n- ') : value
          return `## ${fieldLabel}\n\n${formattedValue}`
        }).join('\n\n')}`
    })
    .join('\n\n---\n\n')

  return (
    <div className="space-y-4">
      {showHeader && (
        <>
          {/* Header */}
          <div className={`flex items-center justify-between p-4 rounded-lg ${isDarkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-50'}`}>
            <div className="flex items-center gap-3">
              <span className="text-2xl">📄</span>
              <div>
                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Invention Record Metadata
                </h2>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {metadata.title || 'Extracted metadata information'}
                </p>
              </div>
            </div>

            {/* Global Actions */}
            <div className="flex gap-2">
              <button
                onClick={exportToDocx}
                className={`text-sm px-3 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-green-700 hover:bg-green-600 text-green-100' : 'bg-green-500 hover:bg-green-600 text-white'}`}
                title="Export invention record metadata as Word document"
              >
                <Download className="w-4 h-4" />
                Export DOCX
              </button>
              <button
                onClick={() => copyToClipboard(allSectionsText)}
                className={`text-sm px-3 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
              >
                <Copy className="w-4 h-4" />
                Copy All
              </button>
            </div>
          </div>

          {/* Expand/Collapse All Controls */}
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => {
                const allExpanded = {}
                allSections.forEach(sectionKey => {
                  allExpanded[sectionKey] = true
                })
                setExpandedSections(allExpanded)
              }}
              className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
            >
              Expand All
            </button>
            <button
              onClick={() => {
                const allCollapsed = {}
                allSections.forEach(sectionKey => {
                  allCollapsed[sectionKey] = false
                })
                setExpandedSections(allCollapsed)
              }}
              className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
            >
              Collapse All
            </button>
          </div>
        </>
      )}

      {/* Sections */}
      <div className="space-y-4">
        {Object.entries(sectionConfigs).map(([sectionKey, config]) => renderSection(sectionKey, config))}
      </div>
    </div>
  )
}

export default IRMetadataDisplay